import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MouseEvent, ReactNode } from "react";
import "./adsbanners.css";

/**
 * The 2d shell: ads keep their gradients — the one sanctioned exception to the
 * token system — but they all share the slot geometry (300 × 100 mobile,
 * 728 × 90 leaderboard, filled exactly and never grown), the shine sweep, the
 * whole-tile tap target and the dismiss cross. Each variant hands in its two
 * gradient classNames, one per mode; the caller picks with useResolvedMode.
 */
function AdTile({
  onOpen,
  onDismiss,
  className,
  native,
  dismissLight,
  children,
  testId,
}: {
  onOpen: () => void;
  /** Absent on underage_verification — the one gate that never dismisses. */
  onDismiss?: () => void;
  /** The gradient, border and shadow for the resolved mode. */
  className: string;
  /** Adds the dotted radial overlay the webcam tile ships with. */
  native?: boolean;
  /** Cross colour on pale (family B light) tiles, where white would vanish. */
  dismissLight?: boolean;
  children: ReactNode;
  testId?: string;
}) {
  const dismiss = (event: MouseEvent) => {
    event.stopPropagation();
    onDismiss?.();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testId}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      className={
        "insite-ads-banner-card mx-auto w-full max-w-[300px] h-[100px] sm:max-w-[728px] sm:h-[90px] " +
        "rounded-2xl text-left transition-transform duration-300 hover:-translate-y-1 cursor-pointer " +
        (native ? "insite-ads-banner-card-native " : "") +
        className
      }
    >
      <div className="insite-ads-banner-shine" />
      {onDismiss && (
        <button
          type="button"
          aria-label="close"
          data-testid="ad-dismiss"
          onClick={dismiss}
          className={
            "absolute top-1.5 right-1.5 z-10 h-7 w-7 rounded-full flex items-center justify-center transition-colors " +
            (dismissLight
              ? "text-black/45 hover:bg-black/10 hover:text-black/70"
              : "text-white/70 hover:bg-white/15 hover:text-white")
          }
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
      <div className="relative z-[1] h-full flex items-center gap-3 px-4 sm:px-6 pr-9 sm:pr-10">
        {children}
      </div>
    </div>
  );
}

export default AdTile;
